/**
 * Unified error handling utilities for API responses
 */

/**
 * Error codes returned by the API for access denied scenarios
 */
export const ACCESS_DENIED_CODES = {
  RESOURCE_ACCESS_DENIED: "RESOURCE_ACCESS_DENIED",
  HTTP_403: "HTTP_403",
} as const;

/**
 * Internal error code used by frontend to indicate access denied state
 */
export const ACCESS_DENIED_ERROR = "ACCESS_DENIED" as const;

/**
 * Represents the structured error response format from the API
 */
export interface ApiErrorResponse {
  status?: string;
  detail?: {
    code?: string;
    message?: string;
    resource_type?: string;
    resource_id?: string;
  } | string;
  error?: {
    code?: string;
    message?: string | object;
    details?: any;
  };
  metadata?: {
    timestamp?: string;
    request_id?: string;
  };
}

/**
 * Result of access denied check
 */
export interface AccessDeniedCheckResult {
  isAccessDenied: boolean;
  resourceType?: string;
  resourceId?: string;
  message?: string;
}

/**
 * Checks if an error represents an "Access Denied" (403) response.
 * Handles both the old string-based error format and the new structured format.
 * 
 * @param error - The error object (typically from a catch block)
 * @returns AccessDeniedCheckResult with isAccessDenied flag and optional resource details
 * 
 * @example
 * ```ts
 * try {
 *   await fetchPortfolioData(id);
 * } catch (error) {
 *   const { isAccessDenied, resourceType, resourceId } = isAccessDeniedError(error);
 *   if (isAccessDenied) {
 *     // Show access denied UI
 *   }
 * }
 * ```
 */
export function isAccessDeniedError(error: any): AccessDeniedCheckResult {
  // Default result
  const result: AccessDeniedCheckResult = {
    isAccessDenied: false,
  };

  if (!error) {
    return result;
  }

  // Check HTTP status code directly
  if (error?.response?.status === 403) {
    result.isAccessDenied = true;
  }

  // Get error message string
  const errorMessage = error instanceof Error ? error.message : String(error);

  // Get response data (could be from axios or fetch)
  const responseData: ApiErrorResponse | undefined = 
    error?.response?.data || error?.data;

  if (responseData) {
    // Check for structured detail object with code
    if (typeof responseData.detail === "object" && responseData.detail !== null) {
      const detailCode = responseData.detail.code;
      if (detailCode === ACCESS_DENIED_CODES.RESOURCE_ACCESS_DENIED) {
        result.isAccessDenied = true;
        result.resourceType = responseData.detail.resource_type;
        result.resourceId = responseData.detail.resource_id;
        result.message = responseData.detail.message;
      }
    }

    // Check for error wrapper code
    if (responseData.error?.code === ACCESS_DENIED_CODES.HTTP_403) {
      result.isAccessDenied = true;
    }

    // Check for string detail containing 403 or Access denied (legacy format)
    if (typeof responseData.detail === "string") {
      if (
        responseData.detail.includes("403") ||
        responseData.detail.toLowerCase().includes("access denied")
      ) {
        result.isAccessDenied = true;
        result.message = responseData.detail;
      }
    }
  }

  // Fallback: check error message for 403 or "Access denied" keywords
  if (!result.isAccessDenied) {
    if (
      errorMessage.includes("403") ||
      errorMessage.toLowerCase().includes("access denied")
    ) {
      result.isAccessDenied = true;
      result.message = errorMessage;
    }
  }

  return result;
}

/**
 * Simple boolean check for access denied errors
 * Use this when you only need to know if it's an access denied error
 * 
 * @param error - The error object
 * @returns true if the error is an access denied (403) error
 */
export function checkAccessDenied(error: any): boolean {
  return isAccessDeniedError(error).isAccessDenied;
}

/**
 * True when the API returned HTTP 403 with structured `RESOURCE_ACCESS_DENIED` detail.
 * Stricter than {@link isAccessDeniedError}: used for Sentry filtering so generic
 * 403 envelopes (`error.code` of `HTTP_403` with a string detail) still report.
 */
export function hasStructuredResourceAccessDeniedPayload(
  error: unknown,
): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  const axiosError = error as { response?: { status?: number; data?: ApiErrorResponse } };
  if (axiosError.response?.status !== 403) {
    return false;
  }

  const responseData = axiosError.response.data;
  if (!responseData) {
    return false;
  }

  return (
    typeof responseData.detail === "object" &&
    responseData.detail !== null &&
    responseData.detail.code === ACCESS_DENIED_CODES.RESOURCE_ACCESS_DENIED
  );
}

/**
 * Extracts a user-friendly error message from an API error response
 * 
 * @param error - The error object
 * @param defaultMessage - Default message if none can be extracted
 * @returns A user-friendly error message
 */
export function getErrorMessage(
  error: any,
  defaultMessage: string = "An unexpected error occurred"
): string {
  if (!error) {
    return defaultMessage;
  }

  // Check for structured response
  const responseData: ApiErrorResponse | undefined = 
    error?.response?.data || error?.data;

  if (responseData) {
    // Structured detail message
    if (typeof responseData.detail === "object" && responseData.detail?.message) {
      return responseData.detail.message;
    }
    // String detail
    if (typeof responseData.detail === "string") {
      return responseData.detail;
    }
    // Error wrapper message
    if (typeof responseData.error?.message === "string") {
      return responseData.error.message;
    }
  }

  // Error instance message
  if (error instanceof Error) {
    return error.message;
  }

  // String error
  if (typeof error === "string") {
    return error;
  }

  return defaultMessage;
}

/**
 * User-facing message for failed page/surface loads (network, API, axios).
 */
export function getLoadErrorMessage(
  error: unknown,
  defaultMessage = "Something went wrong while loading",
): string {
  const message = getErrorMessage(error, defaultMessage);

  if (
    message === "Network Error" ||
    message.includes("ERR_FAILED") ||
    message.includes("AxiosError: Network Error")
  ) {
    return "Couldn't reach the server. The API may be down or still restarting.";
  }

  if (message.startsWith("AxiosError: ")) {
    return message.slice("AxiosError: ".length);
  }

  return message;
}
